/**
 * Client-side visitor tracking + cookie consent for the buyer site.
 *
 * Consent-first: nothing here tracks anything unless the visitor granted the
 * "analytics" cookie category. The consent banner (CookieConsent.tsx) is the
 * single writer of consent; this module reads it and gates all behavior.
 *
 * What we capture (only with analytics consent):
 *   - a stable anonymous visitor id (first-party cookie, 12 months)
 *   - a session id (30-min inactivity window)
 *   - page views, plus mapped product/search/cart/order events
 *   - first- and last-touch referrer + UTM parameters, screen, language, tz
 *
 * Events are queued and flushed in small batches to POST /api/bf/track via
 * fetch(keepalive) — and via navigator.sendBeacon on page hide so the last
 * events are not lost. All failures are swallowed; tracking never disrupts UX.
 *
 * When the visitor is signed in we also attach their Firebase ID token to the
 * batch, so the backend can verify (never merely believe) which account the
 * anonymous visitor id belongs to and sales can see the person behind a
 * journey.
 */
import { onIdTokenChanged } from "firebase/auth";
import { firebaseAuth } from "./firebase";

export type ConsentCategories = {
  necessary: boolean; // always true; the site can't function without these
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

export type ConsentRecord = ConsentCategories & {
  version: number;
  updatedAt: string;
};

// Bump when the cookie policy/categories change to re-prompt returning users.
export const CONSENT_VERSION = 1;

const CONSENT_COOKIE = "kono_consent";
const VISITOR_COOKIE = "kono_vid";
const VISIT_COUNT_COOKIE = "kono_visits";
const VISIT_COUNTED_KEY = "kono_visit_counted";
const SESSION_KEY = "kono_sid";
const SESSION_TS_KEY = "kono_sid_ts";
const FIRST_TOUCH_KEY = "kono_first_touch";
const SESSION_MAX_IDLE_MS = 30 * 60 * 1000;
const ONE_YEAR_S = 365 * 24 * 60 * 60;

export const CONSENT_CHANGED_EVENT = "kono-consent-changed";

// ---- low-level cookie helpers -----------------------------------------------

const readCookie = (name: string): string | undefined => {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name}=([^;]*)`),
  );
  return match ? decodeURIComponent(match[1]) : undefined;
};

const writeCookie = (name: string, value: string, maxAgeS: number) => {
  if (typeof document === "undefined") return;
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie =
    `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeS}; Path=/; ` +
    `SameSite=Lax${secure}`;
};

const deleteCookie = (name: string) => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; Path=/`;
};

// ---- consent ----------------------------------------------------------------

export const getConsent = (): ConsentRecord | null => {
  const raw = readCookie(CONSENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (parsed.version !== CONSENT_VERSION) return null; // re-prompt on policy change
    return { ...parsed, necessary: true };
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = (): boolean => getConsent()?.analytics === true;
export const hasFunctionalConsent = (): boolean =>
  getConsent()?.functional === true;
export const hasMarketingConsent = (): boolean => getConsent()?.marketing === true;

/** All categories on — the default the banner presents. */
export const allConsentGranted = (): ConsentCategories => ({
  necessary: true,
  functional: true,
  analytics: true,
  marketing: true,
});

/** Only the indispensable category. */
export const necessaryOnlyConsent = (): ConsentCategories => ({
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
});

export const saveConsent = (categories: ConsentCategories): ConsentRecord => {
  const record: ConsentRecord = {
    ...categories,
    necessary: true,
    version: CONSENT_VERSION,
    updatedAt: new Date().toISOString(),
  };
  writeCookie(CONSENT_COOKIE, JSON.stringify(record), ONE_YEAR_S);
  try {
    localStorage.setItem(CONSENT_COOKIE, JSON.stringify(record));
  } catch {
    /* ignore */
  }
  applyConsentSideEffects(record);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(CONSENT_CHANGED_EVENT, { detail: record }),
    );
  }
  return record;
};

/**
 * Enforce a consent decision on stored data: when a category is revoked we
 * remove the cookies/ids it justified. Analytics off -> forget the visitor id
 * and stop the tracker; only "necessary" data may persist without consent.
 */
const applyConsentSideEffects = (record: ConsentRecord) => {
  if (!record.functional) {
    deleteCookie(VISIT_COUNT_COOKIE);
  }
  if (!record.analytics) {
    deleteCookie(VISITOR_COOKIE);
    try {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_TS_KEY);
    } catch {
      /* ignore */
    }
    queue.length = 0;
  }
};

// ---- how often this browser has been here -----------------------------------

/**
 * How many separate visits this browser has made, counting the current one.
 *
 * Used to stop asking a returning visitor the same thing over and over. It is a
 * functional cookie, not an analytics one: it identifies nobody and is read
 * only to decide whether to show a prompt. Without functional consent we cannot
 * remember anything between visits, so this reports 0 and the caller is left
 * with whatever it can decide within the session.
 */
export const visitCount = (): number => {
  if (!hasFunctionalConsent()) return 0;
  const raw = Number(readCookie(VISIT_COUNT_COOKIE) || 0);
  return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 0;
};

/**
 * Counts this visit, once per browser session, and returns the new total.
 *
 * Safe to call on every page: the session marker is what makes a ten-page
 * browse one visit rather than ten.
 */
export const recordVisit = (): number => {
  if (!hasFunctionalConsent()) return 0;
  let alreadyCounted = false;
  try {
    alreadyCounted = sessionStorage.getItem(VISIT_COUNTED_KEY) === "1";
  } catch {
    // Storage blocked: counting once per page load is the closest we can get.
  }
  if (alreadyCounted) return visitCount();

  const next = visitCount() + 1;
  writeCookie(VISIT_COUNT_COOKIE, String(next), ONE_YEAR_S);
  try {
    sessionStorage.setItem(VISIT_COUNTED_KEY, "1");
  } catch {
    /* ignore */
  }
  return next;
};

// ---- visitor + session ids --------------------------------------------------

const uuid = (): string => {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
      return crypto.randomUUID().replace(/-/g, "");
    }
  } catch {
    /* fall through */
  }
  return (
    Date.now().toString(36) + Math.random().toString(36).slice(2, 14)
  ).slice(0, 32);
};

const getVisitorId = (): string => {
  let id = readCookie(VISITOR_COOKIE);
  if (!id) {
    id = uuid();
    writeCookie(VISITOR_COOKIE, id, ONE_YEAR_S);
  }
  return id;
};

/** Returns { sessionId, isNew } applying the 30-min inactivity window. */
const getSession = (): { sessionId: string; isNew: boolean } => {
  let sessionId: string | null = null;
  let isNew = false;
  try {
    sessionId = sessionStorage.getItem(SESSION_KEY);
    const lastTs = Number(sessionStorage.getItem(SESSION_TS_KEY) || 0);
    const idle = Date.now() - lastTs;
    if (!sessionId || idle > SESSION_MAX_IDLE_MS) {
      sessionId = uuid();
      isNew = true;
    }
    sessionStorage.setItem(SESSION_KEY, sessionId);
    sessionStorage.setItem(SESSION_TS_KEY, String(Date.now()));
  } catch {
    // sessionStorage blocked: fall back to a per-load ephemeral session.
    sessionId = sessionId || uuid();
    isNew = true;
  }
  return { sessionId, isNew };
};

/**
 * The ids the backend needs to tie a server-side event (such as a search) to
 * this visitor's product views, cart adds and orders. Returns nothing without
 * analytics consent, matching the rest of the tracking.
 */
export const getTrackingIds = (): { visitorId: string; sessionId: string } | undefined => {
  if (!hasAnalyticsConsent()) return undefined;
  try {
    return { visitorId: getVisitorId(), sessionId: getSession().sessionId };
  } catch {
    return undefined;
  }
};

// ---- context (UTM, first touch, screen) -------------------------------------

const parseUtm = (): Record<string, string> => {
  const utm: Record<string, string> = {};
  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of ["source", "medium", "campaign", "term", "content"]) {
      const v = params.get(`utm_${key}`);
      if (v) utm[key] = v.slice(0, 200);
    }
    if (params.get("gclid")) utm.source = utm.source || "google-ads";
    if (params.get("fbclid")) utm.source = utm.source || "facebook";
  } catch {
    /* ignore */
  }
  return utm;
};

/** First-touch attribution: store the first referrer + UTM we ever saw. */
const rememberFirstTouch = (utm: Record<string, string>) => {
  try {
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return;
    localStorage.setItem(
      FIRST_TOUCH_KEY,
      JSON.stringify({
        referrer: document.referrer || "",
        utm,
        landingPath: window.location.pathname,
        at: new Date().toISOString(),
      }),
    );
  } catch {
    /* ignore */
  }
};

// ---- where this tab came from (no consent needed) ---------------------------

const ENTRY_KEY = "kono_entry";

type Touch = {
  referrer: string;
  utm: Record<string, string>;
  landingPath: string;
  at: string;
};

/**
 * Remembers, once per tab, the page the visitor arrived on and where from
 * (referrer + UTM). sessionStorage only, no identifier, nothing sent anywhere
 * by itself: it is read back only when the visitor fills in a form with
 * their contact details (an inquiry), so sales knows how they found us. That
 * is first-party form data, not tracking, so it does not wait for the banner.
 */
export const rememberEntry = () => {
  try {
    if (sessionStorage.getItem(ENTRY_KEY)) return;
    const touch: Touch = {
      referrer: document.referrer || "",
      utm: parseUtm(),
      landingPath: window.location.pathname + window.location.search,
      at: new Date().toISOString(),
    };
    sessionStorage.setItem(ENTRY_KEY, JSON.stringify(touch));
  } catch {
    /* ignore */
  }
};

const readJson = <T>(storage: Storage | undefined, key: string): T | null => {
  try {
    const raw = storage?.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

/**
 * What to attach to an inquiry so sales can see how the person found us:
 * this tab's entry page always; the browser's first visit and the visitor /
 * session ids only with analytics consent (they tie into the journey).
 */
export const inquirySource = () => {
  if (typeof window === "undefined") return undefined;
  const consent = getConsent();
  const analytics = consent?.analytics === true;
  let inviteToken: string | null = null;
  try {
    const m = (window.location.pathname + " " + (readJson<Touch>(sessionStorage, ENTRY_KEY)?.landingPath || "")).match(/\/(?:welcome|w)\/([A-Za-z0-9_-]{8,})/);
    inviteToken = m ? m[1] : null;
  } catch {
    /* ignore */
  }
  return {
    entry: readJson<Touch>(typeof sessionStorage !== "undefined" ? sessionStorage : undefined, ENTRY_KEY),
    firstTouch: analytics ? readJson<Touch>(typeof localStorage !== "undefined" ? localStorage : undefined, FIRST_TOUCH_KEY) : null,
    visitorId: analytics ? getVisitorId() : null,
    sessionId: analytics ? getSession().sessionId : null,
    pageUrl: window.location.href,
    analyticsConsent: consent ? analytics : null,
    inviteToken,
  };
};

// ---- event queue + flush ----------------------------------------------------

type QueuedEvent = {
  type: string;
  ts: string;
  path?: string;
  url?: string;
  referrer?: string;
  title?: string;
  utm?: Record<string, string>;
  properties?: Record<string, unknown>;
  email?: string;
};

const queue: QueuedEvent[] = [];
let started = false;
let flushTimer: ReturnType<typeof setInterval> | null = null;

// Latest signed-in identity, kept fresh by onIdTokenChanged. The token travels
// in the batch body (not a header) because navigator.sendBeacon cannot set
// headers, and the last flush of a session is a beacon.
let authIdToken: string | undefined;
let identifiedUid: string | undefined;

const trackUrl = (): string =>
  `${process.env.REACT_APP_BF_API_BASE_URL || ""}/track`;

const buildPayload = (events: QueuedEvent[]) => ({
  visitorId: getVisitorId(),
  sessionId: getSession().sessionId,
  consent: getConsent(),
  language: navigator.language,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  screen: { width: window.screen?.width, height: window.screen?.height },
  idToken: authIdToken,
  events,
});

/**
 * Keeps the signed-in identity attached to the visitor stream: refreshes the
 * ID token we send with each batch and emits one `identify` per signed-in
 * account so the visitor summary carries the buyer's email.
 */
const watchSignedInUser = () => {
  onIdTokenChanged(firebaseAuth, (user) => {
    if (!user) {
      authIdToken = undefined;
      identifiedUid = undefined;
      return;
    }
    user
      .getIdToken()
      .then((token) => {
        authIdToken = token;
        if (user.uid !== identifiedUid) {
          identifiedUid = user.uid;
          if (user.email) identifyVisitor(user.email);
        }
      })
      .catch(() => {
        /* swallow: tracking must never disrupt the session */
      });
  });
};

const flush = (useBeacon = false) => {
  if (!hasAnalyticsConsent() || queue.length === 0) return;
  const events = queue.splice(0, queue.length);
  const payload = buildPayload(events);
  const url = trackUrl();
  try {
    if (useBeacon && typeof navigator.sendBeacon === "function") {
      const ok = navigator.sendBeacon(
        url,
        new Blob([JSON.stringify(payload)], { type: "application/json" }),
      );
      if (!ok) queue.unshift(...events); // re-queue on failure
      return;
    }
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      /* swallow */
    });
  } catch {
    /* swallow */
  }
};

const enqueue = (event: QueuedEvent) => {
  if (!hasAnalyticsConsent()) return;
  queue.push(event);
  if (queue.length >= 10) flush();
};

const baseEvent = (type: string): QueuedEvent => ({
  type,
  ts: new Date().toISOString(),
  path: window.location.pathname,
  url: window.location.href,
  referrer: document.referrer || undefined,
  title: document.title,
});

// ---- public API -------------------------------------------------------------

/**
 * Start tracking (idempotent). Call once analytics consent is present. Emits a
 * session_start on a fresh session and wires periodic + unload flushing.
 */
export const initVisitorTracking = () => {
  if (started || !hasAnalyticsConsent() || typeof window === "undefined") return;
  started = true;

  getVisitorId();
  const utm = parseUtm();
  rememberFirstTouch(utm);
  const { isNew } = getSession();
  if (isNew) {
    enqueue({ ...baseEvent("session_start"), utm: Object.keys(utm).length ? utm : undefined });
  }

  flushTimer = setInterval(() => flush(), 10000);
  watchSignedInUser();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  window.addEventListener("pagehide", () => flush(true));
};

export const trackPageView = (path?: string) => {
  if (!hasAnalyticsConsent()) return;
  if (!started) initVisitorTracking();
  const utm = parseUtm();
  enqueue({
    ...baseEvent("page_view"),
    path: path || window.location.pathname,
    utm: Object.keys(utm).length ? utm : undefined,
  });
};

export const trackEvent = (
  type: string,
  properties?: Record<string, unknown>,
) => {
  if (!hasAnalyticsConsent()) return;
  if (!started) initVisitorTracking();
  enqueue({ ...baseEvent(type), properties });
};

/**
 * The item as the buyer saw it, attached to product-detail views and cart
 * adds so the server-side interest records (price-drop / back-in-stock
 * watch, sales follow-ups) know vendor, price and stock at that moment.
 */
export const productInterestProps = (
  product: {
    id?: string;
    summary?: string;
    supplierId?: string;
    supplierName?: string;
    price?: { value?: number; currency?: string; unit?: string } | null;
    priceEstimated?: boolean;
    quantity?: { value?: number; unit?: string } | null;
    status?: string;
    hiddenFromBuyerUI?: boolean;
  } | null | undefined,
): Record<string, unknown> => {
  if (!product?.id) return {};
  const id = String(product.id);
  const supplierId =
    product.supplierId || (id.includes("__") ? id.split("__")[0] : undefined);
  const qty = product.quantity?.value;
  const inStock =
    product.status !== "inactive" &&
    !product.hiddenFromBuyerUI &&
    (typeof qty !== "number" || qty > 0);
  return {
    productId: id,
    summary: product.summary ? String(product.summary).slice(0, 600) : undefined,
    supplierId,
    supplierName: product.supplierName || undefined,
    price:
      product.price && typeof product.price.value === "number"
        ? {
            value: product.price.value,
            currency: product.price.currency || "USD",
            unit: product.price.unit || "",
          }
        : undefined,
    priceEstimated: product.priceEstimated === true,
    inStock,
    quantityOnHand:
      typeof qty === "number"
        ? { value: qty, unit: product.quantity?.unit || "" }
        : undefined,
  };
};

export const identifyVisitor = (email: string) => {
  if (!hasAnalyticsConsent() || !email) return;
  if (!started) initVisitorTracking();
  enqueue({ ...baseEvent("identify"), email });
  flush();
};

/**
 * Map the site's existing analytics event names to visitor-stream event types,
 * so key funnel actions (search, product view, add to cart, order) are captured
 * without touching every call site. Returns true if handled.
 */
export const trackMappedAnalyticsEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
): void => {
  if (!hasAnalyticsConsent()) return;
  const map: Record<string, string> = {
    search_query_submitted: "search",
    viewed_product_details: "product_view",
    product_added_to_cart: "add_to_cart",
    viewed_cart: "begin_checkout",
    order_created: "order_created",
  };
  const type = map[eventName];
  if (type) trackEvent(type, properties);
};
